import { select, call, takeLatest, put } from 'redux-saga/effects';

import { SeanceReportActions } from '../constants';
import { SeanceReportApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteSeanceReportAction,
    GetSeanceReportAction,
    GetSeanceReportsAction,
} from '../actions/seanceReport-actions';
import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure, successType } from '../../services/api-handlers/api-resolver';
import { groupSeanceReportToCsv } from '../../helpers/seanceReport/groupCsv';
import { groupSeanceReportToTotal } from '../../helpers/seanceReport/groupTotal';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';

function* handleFetchSeanceReports(action: GetSeanceReportsAction) {
    const oldData = yield select((state: StoreState) => state.seanceReports.data);
    const { filters, sorting, page, itemsPerPage, notTotal } = action.payload;

    if (filters?.caseType && filters.caseType === 'all') {
        delete filters.caseType;
    }
    if (!notTotal) {
        const actionFiles: GetSeanceReportsAction = {
            type: SeanceReportActions.FETCH_SEANCE_REPORTS_TOTAL,
            payload: {
                filters,
                page: 1,
            },
        };
        yield call(handleFetchSeanceReportsTotal, actionFiles);
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        SeanceReportApi.getSeanceReports({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page: page ? page : 1,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchSeanceReportsCsv(action: GetSeanceReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            SeanceReportApi.getSeanceReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetSeanceReportsAction = {
                    type: SeanceReportActions.FETCH_SEANCE_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchSeanceReportsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupSeanceReportToCsv(dataAll);
                    exportToCsv('session-report.csv', groupCsv);
                } else {
                    const groupCsvData = groupSeanceReportToCsv(data.payload);
                    exportToCsv('session-report.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}
function* handleFetchSeanceReportsTotal(action: GetSeanceReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            SeanceReportApi.getSeanceReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 80) {
                const actionFiles: GetSeanceReportsAction = {
                    type: SeanceReportActions.FETCH_SEANCE_REPORTS_TOTAL,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 80,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchSeanceReportsTotal, actionFiles);
            } else {
                if (action.all) {
                    const groupData = groupSeanceReportToTotal(action.all);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                } else {
                    const groupData = groupSeanceReportToTotal(data.payload);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchSeanceReport(action: GetSeanceReportAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => SeanceReportApi.getSeanceReports(id));
}

function* handleDeleteSeanceReports(action: DeleteSeanceReportAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => SeanceReportApi.deleteSeanceReport(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(SeanceReportActions.FETCH_SEANCE_REPORTS, handleFetchSeanceReports);
    yield takeLatest(SeanceReportActions.FETCH_SEANCE_REPORTS_CSV, handleFetchSeanceReportsCsv);
    yield takeLatest(SeanceReportActions.FETCH_SEANCE_REPORTS_TOTAL, handleFetchSeanceReportsTotal);
    yield takeLatest(SeanceReportActions.FETCH_SEANCE_REPORT, handleFetchSeanceReport);
    yield takeLatest(SeanceReportActions.DELETE_SEANCE_REPORT, handleDeleteSeanceReports);
}
